/**
 * Lists all the loadable scripts (non-Emoji or non-picture-based).
 *
 * Add more scripts when supported (e.g. when added to FALLBACK_FONT_DEF in "glyphs.ts")
 * Examples of more scripts: "han", "symbols"
 */
export const LOADABLE_SCRIPTS = ["en", "ext", "he", "arab", "cyr", "grk", "han", "music"] as const;

export const NON_LOADABLE_SCRIPTS = ["space", "emoji", "unknown"] as const;
type LoadableScriptsType = typeof LOADABLE_SCRIPTS;
type NonLoadableScriptsType = typeof NON_LOADABLE_SCRIPTS;

export type LoadableScript = LoadableScriptsType[number];
export type NonLoadableScript = NonLoadableScriptsType[number];
export type Script = LoadableScript | NonLoadableScript;
type ScriptTest = [RegExp | RegExp[], Script];

const SCRIPTS: ScriptTest[] = [
  // See list of supported "Script"s in https://www.unicode.org/reports/tr24/#Script_Values_Table and https://en.wikipedia.org/wiki/Script_(Unicode)
  //
  // See list of unicode block ranges (for unicode characters not supported by a "Script"):
  //  https://unicode-table.com/en/blocks/
  //

  // NOTE: We need to check Symbols before anything else, because some of the symbols match
  //       the `\p{Script=Common}` RegEx.
  // [/[\u{1F300}-\u{1F5FF}]/iu, "symbols" /* Miscellaneous Symbols and Pictographs */],
  [/[\u{1D100}-\u{1D1FF}]/iu, "music" /* Musical Symbols */],

  // NOTE: Emoji needs to be after all the unicode symbols, because "\p{So}" looks for all Unicode characters of
  //       category “Other Symbol” which also includes the above "symbols" and "music".
  //       It also needs to be before "en" as some emojis match the `\p{Script=Common}` RegEx.
  [/[\p{So}\uFE0F\u200D\p{Sk}]/iu, "emoji"],
  //
  [/[\p{ASCII}\p{Script=Common}\p{White_Space}]/iu, "en"],
  [[/[\p{Script=Latin}]/iu, /[^\p{ASCII}\p{Script=Common}\p{White_Space}]/iu], "ext"],
  [/\p{Script=Hebrew}/iu, "he"],
  [/\p{Script=Arabic}/iu, "arab"],
  [/\p{Script=Han}/iu, "han"],
  [/\p{Script=Cyrillic}/iu, "cyr"],
  [/\p{Script=Greek}/iu, "grk"]
  // [/\p{Script=Armenian}/iu, "XXX"],
  // [/\p{Script=Coptic}/iu, "XXX"],
  // [/\p{Script=Armenian}/iu, "XXX"],
  // [/\p{Script=Georgian}/iu, "georgian"],
  // [/\p{Script=Syriac}/iu, "XXX"],
];

// cache here
const _scc: { [idx: string]: Script } = {};
export function scriptFromChar(char: string, withSpace: boolean = false): Script {
  // if not in our cache
  if (!_scc[char]) {
    // Special case that we try and detect if we have whitespace
    if (isSpace(char)) {
      _scc[char] = "space";
    } else {
      // regular pass through scripts
      for (let s of SCRIPTS) {
        const [regex, type] = s;

        const tests = Array.isArray(regex) ? regex : [regex];

        if (tests.every((r) => r.test(char))) {
          _scc[char] = type;
          break;
        }
      }
    }

    _scc[char] = _scc[char] || "unknown";
  }

  // When we compile, we don't want spaces as a "language"
  if (!withSpace && _scc[char] === "space") {
    return "en";
  }

  return _scc[char];
}

export function scriptsFromText(text: string, onlyLoadableScript: boolean = false): Script[] {
  const set: Set<Script> = new Set();

  for (let char of text) {
    const script = scriptFromChar(char);
    if (onlyLoadableScript && NON_LOADABLE_SCRIPTS.includes(script as NonLoadableScript)) {
      continue;
    }
    set.add(script);
  }

  return Array.from(set);
}

export function scriptFromCodePoint(codePoint: number): Script {
  if (!codePoint || Number.isNaN(codePoint)) {
    return "unknown";
  }

  return scriptFromChar(String.fromCodePoint(codePoint));
}

export function isSpace(char: string): boolean {
  return /\p{White_Space}/iu.test(char);
}
